import { Button, Stack } from '@mui/material';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { CourseApi } from '../../api/CourseApi';
import { DialogApi } from '../../api/DialogApi';
import { DialogPromptSelectNextDialog } from '../../api/DialogPromptApi';
import { LessonApi } from '../../api/LessonApi';
import { INextDialog } from '../../api/NextDialog';
import { IUser, UserApi } from '../../api/UserApi';
import AppBarContext from '../../context/AppBarContext';
import DialogPromptFooterContext from '../../context/DialogPromptFooterContext';
import ChatBubble from '../ChatBubble';
import { handleErrorMessage } from '../ErrorDialog';
import { RegistrationReminder, getAudioByText } from './DialogPromptComponent';

interface DialogPromptSelectNextDialogComponentProps {
    dialogPrompt: DialogPromptSelectNextDialog;
    setDialogPromptComplete: (dialogPromptId: number) => void;
    showAvatar?: boolean;
    chatBubbleContainerRef: React.RefObject<HTMLDivElement>;
    isSettings?: boolean;
}

const SELECT_NEXT_DIALOG_ROUTE_DELAY_MS = 1500;

const DialogPromptSelectNextDialogComponent: React.FC<DialogPromptSelectNextDialogComponentProps> = ({ dialogPrompt, setDialogPromptComplete, showAvatar = true, chatBubbleContainerRef }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { setActionList, clearFooterList } = React.useContext(DialogPromptFooterContext)

    const [response, setResponse] = React.useState<string | null>(null);

    const [reminderMessage, setReminderMessage] = React.useState<string | null>(null);
    const [reminderMessageDone, setReminderMessageDone] = React.useState(false);
    const [chatBubbleDone, setChatBubbleDone] = React.useState(false);
    const [chatBubbleResponseDone, setChatBubbleResponseDone] = React.useState(false);

    const [nextDialogUrl, setNextDialogUrl] = React.useState<string | null>(null)
    const completedRef = React.useRef(false);

    const [promptAudio, setPromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const [reminderAudio, setReminderAudio] = React.useState<Blob | null | undefined>(undefined);

    React.useEffect(() => {
        clearFooterList()
        getAudioByText(history, setErrorMessage, dialogPrompt.prompt, setPromptAudio)
        getAudioByText(history, setErrorMessage, RegistrationReminder, setReminderAudio)

        // what reminder message do we need? (if any)
        UserApi.get()
            .then(response => {
                // Handle the response
                const user: IUser = response.data;
                if (user.email === undefined || user.email === null || user.read_only_email_validated == false) {
                    setReminderMessage(RegistrationReminder);
                } else {
                    setReminderMessageDone(true);
                }
            })
            .catch(err => {
                setReminderMessageDone(true);
                handleErrorMessage(err, setErrorMessage, history, location);
            });
    }, [clearFooterList, dialogPrompt.prompt, history, location, setErrorMessage]);

    // const handleButtonClick = (nextDialog: INextDialog) => {
    //     setResponse(nextDialog.response_text);
    //     setNextDialogUrl(`/lesson?dialogId=${nextDialog.next_dialog_id}`);
    // }

    const handleButtonClick = React.useCallback((nextDialog: INextDialog) => {
        setResponse(nextDialog.response_text);

        DialogApi.getById(nextDialog.next_dialog_id)
            .then((dialogResponse) => {
                if (dialogResponse.status >= 300) {
                    throw new Error("Unable to DialogApi.getById() " + dialogResponse.status);
                }
                const dialog = dialogResponse.data;

                return LessonApi.getById(dialog.lesson_id)
                    .then((lessonResponse) => {
                        if (lessonResponse.status >= 300) {
                            throw new Error("Unable to LessonApi.getById() " + lessonResponse.status);
                        }
                        const lesson = lessonResponse.data;

                        return CourseApi.getById(lesson.course_id)
                            .then((courseResponse) => {
                                if (courseResponse.status >= 300) {
                                    throw new Error("Unable to CourseApi.getById() " + courseResponse.status);
                                }
                                const course = courseResponse.data;

                                setNextDialogUrl(`/lesson/${course.id}/${lesson.id}/${dialog.id}`);
                            });
                    });
            })
            .catch((err) => {
                handleErrorMessage(err, setErrorMessage, history, location);
            });
    }, [setResponse, setNextDialogUrl, setErrorMessage, history, location]);

    React.useEffect(() => {
        if (chatBubbleDone) {
            setActionList(
                dialogPrompt.next_dialog_list.map((nextDialog) => (
                    <Button variant="contained" color="primary" onClick={() => handleButtonClick(nextDialog)}>
                        {nextDialog.button_text}
                    </Button>
                ))
            )
        }
    }, [chatBubbleDone, dialogPrompt.next_dialog_list, handleButtonClick, setActionList]);

    React.useEffect(() => {
        if (chatBubbleResponseDone && !completedRef.current) {
            completedRef.current = true;
            setDialogPromptComplete(dialogPrompt.id);
        }
    }, [chatBubbleResponseDone, dialogPrompt.id, setDialogPromptComplete])

    React.useEffect(() => {
        if (chatBubbleResponseDone && nextDialogUrl) {
            const timeoutId = window.setTimeout(() => {
                history.push(nextDialogUrl);
            }, SELECT_NEXT_DIALOG_ROUTE_DELAY_MS);
            return () => window.clearTimeout(timeoutId);
        }
    }, [chatBubbleResponseDone, nextDialogUrl, history])

    return (
        <Stack>
            {reminderMessage && (
                <ChatBubble
                    isTeacher={true}
                    message={reminderMessage}
                    messageAudio={reminderAudio ? reminderAudio : null}
                    showAvatar={showAvatar}
                    setChatBubbleDone={setReminderMessageDone}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                />
            )}
            {reminderMessageDone && (
                <ChatBubble
                    isTeacher={dialogPrompt.is_teacher}
                    message={dialogPrompt.prompt}
                    messageAudio={promptAudio ? promptAudio : null}
                    showAvatar={showAvatar}
                    setChatBubbleDone={setChatBubbleDone}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                />
            )}
            {response ? (
                <ChatBubble
                    isTeacher={!dialogPrompt.is_teacher}
                    message={response}
                    messageAudio={null} // no audio for student
                    showAvatar={showAvatar}
                    setChatBubbleDone={setChatBubbleResponseDone}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                />
            ) : null}
        </Stack>
    );
};

export default DialogPromptSelectNextDialogComponent;
